import * as React from 'react';
import MyTypes from 'MyTypes';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, ConnectedProps } from 'react-redux';
import { Card, Form, Row, Col, Spinner } from 'react-bootstrap';
import '../../../assets/styles/myDashboard.css';
import AdjusterInvoices from './AdjusterInvoices';
import SimpleMetricCard from '../SimpleMetricCard';
import ClaimsByCarrierPie from './ClaimsByCarrierPie';  
import HorizontalBarTwoSeries from '../HorizontalBarTwoSeries';
import { BankOutlined, DollarOutlined, CloseCircleOutlined, TruckOutlined, AlertOutlined, TrophyOutlined, FundOutlined, ScheduleOutlined } from '@ant-design/icons';
import { dashboardActions } from 'store/dashboard';
import * as helpers from '../../../utils/helpers'; 
import * as models from 'models';
import { contactsActions } from 'store/contacts';
import { AD_Invoice_Selector, MetricType } from '../../../models/enums'; 
import { payrollActions } from 'store/payroll';
import TabbedTables from './TabbedTables';
import ReportingTable from '../../payroll/ReportingTable'; 
import { claimsActions } from 'store/claims';
import services from '../../../services';
import { error } from 'console';
import { USER_PERMISSIONS } from 'store/enums';

interface Props extends PropsFromRedux {
}

interface State {
    activeTableTab: string; 
    isAdmin: boolean; 
    adjusterId: number; 
    invoiceSelector: AD_Invoice_Selector; 
    lastPayroll: models.PayrollListResponse;
    pieCardHeight: number;
}

class AdjusterDashboard extends React.PureComponent<Props, State> {
    public state = {
        activeTableTab: "Invoices", 
        isAdmin: false,
        adjusterId: -1,
        invoiceSelector: AD_Invoice_Selector.LastTwenty,
        pieCardHeight: 420,
        lastPayroll: {
            totalCount: 0,
            resourceList: [] as models.PayrollSummary[],
            error: {
                code: 0, 
                message: ''
            }, 
            isSuccess: false,
            insuranceCompanies: [] as models.DropDownListItem[],
            fieldAdjusters: [] as models.DropDownListItem[]
        }, 
    }

    tableRef = React.createRef<HTMLDivElement>();
    metricsRef = React.createRef<HTMLDivElement>();
    metricsResizeObserver?: ResizeObserver; 

    public componentDidMount = () => {       
        this.props.getAdjustersDD();
        this.getLastPayroll();
        this.bindMetricsHeightSync();
        if(helpers.userHasPermission(USER_PERMISSIONS.DASHBOARDS_ADJUSTER_ALL)) {
            this.setState({isAdmin: true});        
        }
        else {
            this.props.getDashboardData(this.state.adjusterId, this.state.invoiceSelector); 
            this.props.requestAdjusterFeesList({
                ...models.defaultAdjusterFeeListRequest,   
                hasAdjusterPercent: true,              
            }); 
        }
    }

    public componentDidUpdate = (prevProps: Props) => {
        if (prevProps.isDashboardLoading && !this.props.isDashboardLoading) {
            // Metric text/values may change card height after load.
            this.syncPieHeightToMetrics();
        }
    }

    public componentWillUnmount = () => {
        this.metricsResizeObserver?.disconnect();
        this.setState({adjusterId: -1}); 
        this.props.clearAdjusterDashboard(); 
    }

    bindMetricsHeightSync = () => {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.metricsResizeObserver = new ResizeObserver(() => {
            this.syncPieHeightToMetrics();
        });

        if (this.metricsRef.current) {
            this.metricsResizeObserver.observe(this.metricsRef.current);
            this.syncPieHeightToMetrics();
        }
    }

    syncPieHeightToMetrics = () => {
        const metricsEl = this.metricsRef.current;
        if (!metricsEl) {
            return;
        }

        // Keep a floor so the empty/pre-select donut isn't clipped when metric cards are still short.
        const metricsHeight = Math.ceil(metricsEl.getBoundingClientRect().height);
        const nextHeight = Math.max(420, metricsHeight);
        if (nextHeight > 0 && nextHeight !== this.state.pieCardHeight) {
            this.setState({ pieCardHeight: nextHeight });
        }
    }

    public getLastPayroll = async () => {
        const response = await services.api.payroll.getPayrollList({
            ...models.defaultPayrollListRequest,
            pageSize: 1,
            sort: "-runDate",
        }); 
        if(response) {
            this.setState({lastPayroll: response.data}); 
        }
    }

    public handleAdjusterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const adjusterId = parseInt(e.target.value);
        this.setState({adjusterId: adjusterId}); 
        this.props.getDashboardData(adjusterId, this.state.invoiceSelector); 
        this.props.requestAdjusterFeesList({
            ...models.defaultAdjusterFeeListRequest,                 
            fieldAdjuster: this.props.adjusters.resourceList.find(x => x.id === adjusterId)?.name || "",    
            hasAdjusterPercent: true,         
        }); 
    }    

    public handlePageSizeChange = (v: AD_Invoice_Selector) => {
        this.props.getDashboardData(this.state.adjusterId, v); 
        this.setState({invoiceSelector: v});         
    }

    public handleDownload = () => {    
        this.props.getAdjusterInvoices(this.state.adjusterId, this.state.invoiceSelector); 
    }

    public handleClosedYtdClick = () => {
        const { adjusterId } = this.state;

        if(adjusterId > 0){
            this.props.requestClaims({
                ...this.props.claimListRequest,
                fieldAdjuster: '',
                fieldAdjusterId: adjusterId,
                status: "Closed", 
                closedDateFrom: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
                closedDateTo: new Date().toISOString().split('T')[0], 
            }); 
            services.router.gotoWithState(services.router.url.claimList, {referrer: "dashboard"}); 
        }
    }

    public handleYtdPayrollAmtClick = () => {
        this.props.requestAdjusterFeesList({
            ...models.defaultAdjusterFeeListRequest, 
            fieldAdjuster: this.props.adjusters.resourceList.find(x => x.id === this.state.adjusterId)?.name || "",
            paymentDateFrom: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
            paymentDateTo: new Date().toISOString().split('T')[0], 
            isPayrolled: true,            
        }); 
        this.goToAdjusterTable(); 
    }

    public handleAwaitingCarrierPmtClick = () => {
        this.props.requestAdjusterFeesList({
            ...models.defaultAdjusterFeeListRequest, 
            fieldAdjuster: this.props.adjusters.resourceList.find(x => x.id === this.state.adjusterId)?.name || "",
            hasCarrierBalance: true, 
            hasAdjusterPercent: true,
        }); 
        this.goToAdjusterTable(); 
    }

    public handleExpectedNextClick = () => {
        const lastPayroll = this.state.lastPayroll.resourceList[0]; 

        if(lastPayroll){
            this.props.requestAdjusterFeesList({
                ...models.defaultAdjusterFeeListRequest, 
                fieldAdjuster: this.props.adjusters.resourceList.find(x => x.id === this.state.adjusterId)?.name || "",
                hasAdjusterBalance: true,
                paymentDateFrom: lastPayroll.runDate
            }); 
            this.goToAdjusterTable(); 
        }
    }    

    public goToAdjusterTable = () => {
        this.setState({activeTableTab: "Adjuster Fees"}); 
        this.scrollToSection(this.tableRef); 
    }

    public scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
        if(ref.current) {
            ref.current.scrollIntoView({behavior: 'smooth'}); 
        }
    }

    public render() {
        const { adjusters, data, isAdjustersDDLoading, isDashboardLoading, isAdjusterInvoicesDownloading } = this.props; 
        const { activeTableTab,  adjusterId, invoiceSelector } = this.state;

        const tabbedTableProps: React.ComponentProps<typeof TabbedTables> = {
            activeTab: activeTableTab,
            components: [
                {                
                    tabName: "Invoices",
                    component: 
                        <AdjusterInvoices
                            invoices={data.adjusterInvoices}
                            invoiceSelector={invoiceSelector}
                            isLoading={isDashboardLoading || isAdjusterInvoicesDownloading}
                            handlePageSizeSelect={this.handlePageSizeChange}
                            download={this.handleDownload}
                        />
                }, 
                {
                    tabName: "Adjuster Fees",
                    component: <ReportingTable/>
                }
            ]
        } 

        return (
            <div className="adjuster-dashboard">
                <div className="adjuster-dashboard-top">
                    <div className="adjuster-dashboard-header">
                        {this.state.isAdmin &&
                            <div className="adjuster-dashboard-adjuster-picker">
                                <Form.Label htmlFor="adjusterDashboardAdjuster">Field Adjuster</Form.Label>
                                <Form.Select
                                    id="adjusterDashboardAdjuster"
                                    name="adjuster"
                                    className="adjuster-dashboard-adjuster-select"
                                    value={adjusterId}
                                    onChange={(e) => this.handleAdjusterChange(e)}
                                >
                                    {isAdjustersDDLoading && <option key={-10}>Loading...</option>}
                                    {<option key={-1}>Choose...</option>}
                                    {adjusters.totalCount > 0 && adjusters.resourceList.map((f: models.DropDownListItem, i: number) => (
                                        <option key={i} value={f.id}>{f.name}</option>
                                    ))}
                                </Form.Select>
                                {isDashboardLoading && <Spinner animation="border" role="status" variant="primary" size="sm" />}
                            </div>
                        }
                        {!this.state.isAdmin && <div className="adjuster-dashboard-header-title">Field Adjuster Dashboard</div>}
                    </div>
                    <div className="adjuster-dashboard-top-body">
                        <div className="adjuster-dashboard-metrics" ref={this.metricsRef}>
                            <SimpleMetricCard
                                title={data?.ytdPayrollAmt?.name || 'YTD Payroll Amt'}
                                value={data?.ytdPayrollAmt?.metric ?? 0}
                                type={data?.ytdPayrollAmt?.type || MetricType.Money}
                                icon={<DollarOutlined />}
                                toolTipText='All payroll paid to this adjuster this calendar year — includes both advances and standard payroll runs.'
                                handleClick={this.handleYtdPayrollAmtClick}
                            />
                            <SimpleMetricCard
                                title={data?.ytdAdvancesAmt?.name || 'YTD Advances Amt'}
                                value={data?.ytdAdvancesAmt?.metric ?? 0}
                                type={data?.ytdAdvancesAmt?.type || MetricType.Money}
                                icon={<AlertOutlined />}
                                toolTipText='Advance payroll paid to this adjuster this calendar year (before carrier remittance). This amount is already included in YTD Payroll Amt — do not add the two together.'
                            />
                            <SimpleMetricCard
                                title={data?.closedYtd?.name || 'YTD Closed'}
                                value={data?.closedYtd?.metric ?? 0}
                                type={data?.closedYtd?.type || MetricType.Count}
                                icon={<CloseCircleOutlined />}
                                toolTipText='Number of claims closed for this adjuster this calendar year.'
                                handleClick={this.handleClosedYtdClick}
                            />
                            <SimpleMetricCard
                                title={data?.lifetimeClosed?.name || 'Lifetime Closed'}
                                value={data?.lifetimeClosed?.metric ?? 0}
                                type={data?.lifetimeClosed?.type || MetricType.Count}
                                icon={<TrophyOutlined />}
                                toolTipText='Total number of claims this adjuster has closed across their entire history.'
                            />
                            <SimpleMetricCard
                                title={data?.awaitingCarrierPaymentAmt?.name || 'Awaiting Carrier Pmt'}
                                value={data?.awaitingCarrierPaymentAmt?.metric ?? 0}
                                type={data?.awaitingCarrierPaymentAmt?.type || MetricType.Money}
                                icon={<TruckOutlined />}
                                toolTipText='Adjuster share of fees where the carrier still owes Compass. Gross amount — does not subtract advances or payroll already paid on those fees.'
                                handleClick={this.handleAwaitingCarrierPmtClick}
                            />
                            <SimpleMetricCard
                                title={data?.advancesBalanceAmt?.name || 'Advances Balance'}
                                value={data?.advancesBalanceAmt?.metric ?? 0}
                                type={data?.advancesBalanceAmt?.type || MetricType.Money}
                                icon={<FundOutlined />}
                                toolTipText='Open advance exposure — paid to this adjuster before carrier remittance and not yet offset by collections. Shrinks as carriers pay.'
                            />
                            <SimpleMetricCard
                                title={data?.lastPayrollAmt?.name || 'Last Payroll Amt'}
                                value={data?.lastPayrollAmt?.metric ?? 0}
                                type={data?.lastPayrollAmt?.type || MetricType.Money}
                                icon={<ScheduleOutlined />}
                                toolTipText='Total paid to this adjuster on their most recent standard (non-advance) payroll run.'
                            />
                            <SimpleMetricCard
                                title={data?.awaitingPayrollAmt?.name || 'Expected Next Payroll Amt'}
                                value={data?.awaitingPayrollAmt?.metric ?? 0}
                                type={data?.awaitingPayrollAmt?.type || MetricType.Money}
                                icon={<BankOutlined />}
                                toolTipText='Carrier remittances received since the last standard payroll run that are still owed to this adjuster (current pay-window estimate).'
                                handleClick={(data?.awaitingPayrollAmt?.metric ?? 0) !== 0 ? this.handleExpectedNextClick : undefined}
                            />
                        </div>
                        <Card
                            className="adjuster-dashboard-pie-card"
                            style={{ height: this.state.pieCardHeight }}
                        >
                            <ClaimsByCarrierPie
                                claimsByCarrier={data.openClaimsByCarrier}
                                fieldAdjusterId={adjusterId > 0 ? adjusterId : undefined}
                                height={this.state.pieCardHeight}
                            />
                        </Card>
                    </div>
                </div>
                <Row className="adjuster-dashboard-charts g-3">
                    <Col md={6}>
                        <Card className="adjuster-dashboard-chart-card">
                            <HorizontalBarTwoSeries
                                categories={data.claimsByMonth.map(x => x.month)}
                                columnTitle='Opened'
                                seriesColumn={data.claimsByMonth.map(x => x.claimsOpened)}
                                seriesLine={data.claimsByMonth.map(x => x.claimsClosed)}
                                seriesType='number'
                                lineTitle='Closed'
                                title='Monthly Claim Activity'
                            />
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="adjuster-dashboard-chart-card">
                            <HorizontalBarTwoSeries
                                categories={data.payrollByMonth.map(x => x.month)}
                                columnTitle='Payroll Amt'
                                seriesColumn={data.payrollByMonth.map(x => x.amount)}
                                seriesLine={data.payrollByMonth.map(x => x.advancedAmount)}
                                seriesType='money'
                                lineTitle='Advanced Amt'
                                title='Monthly Payroll'
                            />
                        </Card>
                    </Col>
                </Row>
                <Row as={Card} className="adjuster-dashboard-tables">
                    <div ref={this.tableRef} style={{marginTop: ".5em"}}>
                        <TabbedTables
                            activeTab={activeTableTab}
                            components={tabbedTableProps.components}
                        />
                    </div>
                </Row>
            </div>
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    adjusterFeesRequest: state.payroll.search.adjusterFeeListRequest, 
    isDashboardLoading: state.dashboards.dashboards.isAdjusterDashboardLoading,
    claimListRequest: state.claims.search.claimsRequest, 
    data: state.dashboards.dashboards.adjusterDashboard,
    adjusters: state.contacts.dropdowns.adjustersDD, 
    isAdjustersDDLoading: state.contacts.dropdowns.isAdjustersDDLoading,
    isAdjusterInvoicesDownloading: state.dashboards.dashboards.isInvoicesDownloading,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {            
            clearAdjusterDashboard: dashboardActions.clearAdjusterDashboard, 
            getAdjustersDD: contactsActions.requestAdjustersDD, 
            getAdjusterInvoices: dashboardActions.requestAdjusterInvoices,
            getDashboardData: dashboardActions.requestAdjusterDashboard,    
            requestAdjusterFeesList: payrollActions.requestAdjusterFeesList,          
            requestClaims: claimsActions.requestClaims, 
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AdjusterDashboard); 
